/**
 * Central asset path registry — use these helpers instead of hard-coded `/assets/images/...` paths.
 *
 * Folder layout:
 *   /assets/brand/              Logos and partner marks
 *   /assets/backgrounds/        Login, signup, and full-page backgrounds
 *   /assets/icons/common/       Shared UI icons (navigation, actions, metrics)
 *   /assets/icons/social/       Platform logos and publish-format icons
 *   /assets/icons/ai-asset-manager/  AI Asset Manager toolbar icons
 *   /assets/icons/ui/           Small inline UI icons (calendar, edit, checkbox)
 *   /assets/illustrations/      Property highlights and marketing illustrations
 */

export const PLACEHOLDER_IMAGE = "/placeholder.svg"

/** Absolute URL for static files under `public/` (SVG `<image>`, html2canvas, etc.). */
export function resolvePublicAssetUrl(path) {
  if (!path || typeof path !== "string") return path
  if (path.startsWith("data:") || path.startsWith("http://") || path.startsWith("https://")) {
    return path
  }
  const publicUrl = process.env.PUBLIC_URL || ""
  const normalized = path.startsWith("/") ? path : `/${path}`
  if (typeof window !== "undefined") {
    return `${window.location.origin}${publicUrl}${normalized}`
  }
  return `${publicUrl}${normalized}`
}

export const brand = {
  sohoLogoNew: "/assets/brand/soho_logo_new.svg",
  sohoLogoSfh: "/assets/brand/soho_logo_sfh.svg",
  sohoSocial: "/assets/brand/soho_social.svg",
  rategainLogo: "/assets/brand/rategain_logo.png",
  iconBulb: "/assets/brand/icon-bulb.png",
}

export const backgrounds = {
  login: "/assets/backgrounds/login-bg.jpg",
  signup: "/assets/backgrounds/signup-bg.svg",
}

export const illustrations = {
  pool: "/assets/illustrations/Pool.svg",
  wifi: "/assets/illustrations/Wifi.svg",
  restaurant: "/assets/illustrations/Restaurant.svg",
  sofa: "/assets/illustrations/Sofa.svg",
  reputation: "/assets/illustrations/Reputation.svg",
  sohoImage: "/assets/illustrations/soho-image.png",
  aiPlanning: "/assets/illustrations/aiplanning.png",
  generateAiIcon: "/assets/illustrations/generate-ai-icon.png",
  systemSetup: "/assets/illustrations/system-setup.png",
}

export const aiAssetManagerIcons = {
  arrowLeftRight: "/assets/icons/ai-asset-manager/arrow-left-right.svg",
  edit: "/assets/icons/ai-asset-manager/edit-icon.svg",
  errorInfo: "/assets/icons/ai-asset-manager/error-info-icon.svg",
  leftChevron: "/assets/icons/ai-asset-manager/left-chevron.svg",
  plus: "/assets/icons/ai-asset-manager/plus.svg",
  send: "/assets/icons/ai-asset-manager/send.svg",
  trash: "/assets/icons/ai-asset-manager/trash.svg",
  close: "/assets/icons/ai-asset-manager/x.svg",
}

/** @param {string} filename e.g. "like.svg" */
export function commonIcon(filename) {
  return `/assets/icons/common/${filename}`
}

/** @param {string} filename e.g. "facebook.svg" */
export function socialIcon(filename) {
  return `/assets/icons/social/${filename}`
}

/** @param {string} filename e.g. "calendar-plus.svg" */
export function uiIcon(filename) {
  return `/assets/icons/ui/${filename}`
}

/** @param {string} filename e.g. "Pool.svg" */
export function illustration(filename) {
  return `/assets/illustrations/${filename}`
}

/** Social network API `description` → colored brand icon (same as Calendar / Create Post). */
export function socialTypeIconPath(description) {
  if (!description) return commonIcon("asset.svg")
  const key = String(description).toLowerCase().trim()
  if (key === "twitter" || key === "x") return socialIcon("x.png")
  if (key === "instagram_graph" || key === "instagram") return socialIcon("instagram.svg")
  if (key === "facebook") return socialIcon("facebook.svg")
  if (key === "linkedin") return socialIcon("linkedin.png")
  if (key === "tiktok") return socialIcon("tiktok.png")
  if (key === "youtube") return socialIcon("youtube_1.svg")
  return socialIcon(`${key}.svg`)
}

/** Setup flow channel name → social icon under /assets/icons/social/. */
export function socialChannelIconPath(channelName) {
  const normalized = String(channelName || "").toLowerCase().trim()
  if (!normalized) return commonIcon("social.svg")
  return socialTypeIconPath(normalized)
}

export const ASSET_PATHS = {
  brand,
  backgrounds,
  illustrations,
  aiAssetManagerIcons,
  commonIcon,
  socialIcon,
  uiIcon,
  illustration,
  socialTypeIconPath,
  socialChannelIconPath,
  resolvePublicAssetUrl,
  placeholder: PLACEHOLDER_IMAGE,
}

export default ASSET_PATHS
